import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  BusinessAlarmInfoResponse,
  BusinessUpdateAlarmInfoRequest,
  BusinessMypageInfoResponse,
  BusinessMypageMainResponse,
  BusinessMypageCompanyInfoResponse,
  BusinessMypageCompanyInfoUpdateRequest,
  BusinessMypageCompanyInfoUpdateResponse,
  BusinessUpdateInfoRequest,
  BusinessCompanyMemberResponse,
  BusinessCompanyMemberJoinRequest,
  BusinessCompanyMemberJoinResponse,
  BusinessCompanyMemberInfoResponse,
  BusinessUpdateCompanyMemberPermissionsRequest,
  BusinessCompanyMemberDeleteRequest,
  BusinessCompanyMemberDeleteResponse,
  BusinessProductListResponse,
  BusinessProductUpdateResponse,
  BusinessServiceStatsRequest,
  BusinessServiceStatsResponse,
  BusinessSettlementInfoRequest,
  BusinessSettlementInfoResponse,
  BusinessProductGroup,
  BusinessServiceProvideListResponse,
  BusinessMypageCustomerListRequest,
  BusinessMypageCustomerListResponse,
  BusinessMypageCustomerDetailRequest,
  BusinessMypageCustomerDetailResponse,
  BusinessMypageCustomerTicketListRequest,
  BusinessMypageCustomerTicketListResponse,
  BusinessMypageContractListRequest,
  BusinessMypageContractListResponse,
  BusinessMypageContractDetailRequest,
  BusinessMypageContractDetailResponse,
  BusinessMypageBusinessTimeResponse,
  BusinessMypageBusinessTimeRequest,
  BusinessMypageHolidayResponse,
  BusinessMypageHolidaySaveRequest,
  BusinessMypageReserveDeadlineResponse,
  BusinessMypageReserveDeadlineSettings,
  BusinessMypageDisconnectCompanyResponse,
} from "~/types/common";

export const BUSINESS_MYPAGE_QUERY_KEYS = {
  main: ["business", "mypage", "main"],
  menu: ["business", "mypage", "menu"],
  mypage: ["business", "mypage", "alarm-info"],
  info: ["business", "mypage", "info"],
  companyInfo: ["business", "mypage", "company-info"],
  companyMember: ["business", "mypage", "company-member"],
  companyMemberInfo: ["business", "mypage", "company-member-info"],
  productList: ["business", "mypage", "product-list"],
  serviceProvideList: ["business", "mypage", "service-provide-list"],
  customerList: ["business", "mypage", "customer-list"],
  customerDetail: ["business", "mypage", "customer-detail"],
  petMemo: ["business", "mypage", "pet-memo"],
  customerTicketList: ["business", "mypage", "customer-ticket-list"],
  contractList: ["business", "mypage", "contract-list"],
  contractDetail: ["business", "mypage", "contract-detail"],
  serviceStats: ["business", "mypage", "service-stats"],
  settlementInfo: ["business", "mypage", "settlement-info"],
  ticketList: ["business", "mypage", "ticket-list"],
  searchUser: ["business", "mypage", "search-user"],
  ticketIssueList: ["business", "mypage", "ticket-issue-list"],
  ticketIssueDetail: ["business", "mypage", "ticket-issue-detail"],
  usageHistory: ["business", "mypage", "usage-history"],
} as const;

const getBusinessMypageMenu = async (): Promise<string[]> => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.BUSINESS_MYPAGE.MENU);
    return data.data || [];
  } catch (error) {
    console.error("Business mypage menu API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageMenu = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.menu],
    queryFn: () => getBusinessMypageMenu(),
    select: (data) => data || [],
  });
};

const getBusinessMypageMain = async (): Promise<BusinessMypageMainResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_MAIN,
    );
    return (
      data.data || {
        companyName: "",
        bizUserName: "",
        fileUrl: null,
        noticeMessage: null,
        cdNotiType: null,
        activeStatus: "NOT_REQUESTED",
        serviceProvides: [],
        businessStats: {
          totalReservations: 0,
          todayVisitors: 0,
          monthlyRevenue: 0,
          totalReviews: 0,
        },
      }
    );
  } catch (error) {
    console.error("Business mypage main API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageMain = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.main],
    queryFn: () => getBusinessMypageMain(),
    select: (data) =>
      data || {
        companyName: "",
        bizUserName: "",
        fileUrl: null,
        noticeMessage: null,
        cdNotiType: null,
        activeStatus: "NOT_REQUESTED",
        serviceProvides: [],
        businessStats: {
          totalReservations: 0,
          todayVisitors: 0,
          monthlyRevenue: 0,
          totalReviews: 0,
        },
      },
  });
};

const getBusinessMypageInfo = async (): Promise<BusinessMypageInfoResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_INFO,
    );
    return (
      data.data || {
        bizUserSeq: 0,
        bizUserName: "",
        email: "",
        phoneNumber: "",
        companyName: "",
        businessNumber: "",
        companyAddr: "",
        companyAddrDetail: "",
        mainTelNumber: "",
        cdUserType: "",
      }
    );
  } catch (error) {
    console.error("Business mypage info API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageInfo = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.info],
    queryFn: () => getBusinessMypageInfo(),
    select: (data) =>
      data || {
        bizUserSeq: 0,
        bizUserName: "",
        email: "",
        phoneNumber: "",
        companyName: "",
        businessNumber: "",
        companyAddr: "",
        companyAddrDetail: "",
        mainTelNumber: "",
        cdUserType: "",
      },
  });
};

const getBusinessMypageAlarmInfo =
  async (): Promise<BusinessAlarmInfoResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_ALARM_INFO,
      );
      return (
        data.data || {
          alarmEmailYn: "N",
          alarmMarketingYn: "N",
          alarmNoticeYn: "N",
          alarmSmsYn: "N",
        }
      );
    } catch (error) {
      console.error("Business mypage alarm info API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessMypageAlarmInfo = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.mypage],
    queryFn: () => getBusinessMypageAlarmInfo(),
    select: (data) => data,
  });
};

const updateBusinessMypageAlarmInfo = async (
  request: BusinessUpdateAlarmInfoRequest,
) => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_ALARM_INFO_UPDATE,
      request,
    );
    return data.data;
  } catch (error) {
    console.error("Business mypage alarm info 업데이트 API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateBusinessMypageAlarmInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageAlarmInfo,
    onSuccess: (data, variables) => {
      // 서버 응답 데이터로 직접 캐시 업데이트
      queryClient.setQueryData(
        [
          ...BUSINESS_MYPAGE_QUERY_KEYS.mypage,
          {
            bizUserSeq: variables.bizUserSeq,
          },
        ],
        data,
      );
    },
  });
};

const updateBusinessMypageInfo = async (request: BusinessUpdateInfoRequest) => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_INFO_UPDATE,
      request,
    );
    return data.data;
  } catch (error) {
    console.error("Business mypage info 업데이트 API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateBusinessMypageInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageInfo,
    onSuccess: () => {
      // 정보 업데이트 후 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.info,
      });
    },
  });
};

const getBusinessCompanyMember =
  async (): Promise<BusinessCompanyMemberResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_MEMBER,
      );
      return (
        data.data || {
          members: [],
          totalCount: 0,
        }
      );
    } catch (error) {
      console.error("Business company member API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessCompanyMember = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.companyMember],
    queryFn: () => getBusinessCompanyMember(),
    select: (data) =>
      data || {
        members: [],
        totalCount: 0,
      },
  });
};

const getBusinessMypageProductList = async (
  cdProductType?: string,
): Promise<BusinessProductListResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_PRODUCT_LIST,
      {
        params: {
          cdProductType: cdProductType,
        },
      },
    );
    return {
      data: data.data || [],
    };
  } catch (error) {
    console.error("Business mypage product list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageProductList = (cdProductType?: string) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.productList, cdProductType],
    queryFn: () => getBusinessMypageProductList(cdProductType),
    select: (data) => data.data,
  });
};

const updateBusinessMypageProduct = async (
  request: BusinessProductGroup[],
): Promise<BusinessProductUpdateResponse> => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_PRODUCT_UPDATE,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage product update API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateBusinessMypageProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageProduct,
    onSuccess: () => {
      // 상품 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.productList,
      });
    },
  });
};

const getBusinessMypageCompanyInfo =
  async (): Promise<BusinessMypageCompanyInfoResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_INFO,
      );
      return (
        data.data || {
          companySeq: 0,
          email: "",
          companyName: "",
          businessNumber: "",
          companyAddr: "",
          companyAddrDetail: "",
          mainTelNumber: "",
          mainBank: "",
          mainAccountNumber: "",
          depositor: "",
          playgroundYn: "N",
          serviceProvides: [],
          facilities: [],
          weightClasses: [],
          longitude: null,
          latitude: null,
        }
      );
    } catch (error) {
      console.error("Business mypage company info API 호출 에러:", error);
      throw error;
    }
  };

/**
 * 계정정보(사업장 정보) 조회.
 * 서버가 원장 전용으로 막아둔 API라 선생님 계정이 호출하면 statusCode 4001
 * (BUSINESS_ONLY_ACCESS)이 떨어지고 모든 필드가 null 로 내려온다.
 * 선생님도 진입하는 화면에서는 `enabled: isOwner` 로 막을 것.
 */
export const useBusinessMypageCompanyInfo = (enabled: boolean = true) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.companyInfo],
    queryFn: () => getBusinessMypageCompanyInfo(),
    enabled,
    select: (data) =>
      data || {
        companySeq: 0,
        email: "",
        companyName: "",
        businessNumber: "",
        companyAddr: "",
        companyAddrDetail: "",
        mainTelNumber: "",
        mainBank: "",
        mainAccountNumber: "",
        depositor: "",
        playgroundYn: "N",
        serviceProvides: [],
        facilities: [],
        weightClasses: [],
        longitude: null,
        latitude: null,
      },
  });
};

const updateBusinessMypageCompanyInfo = async (
  request: BusinessMypageCompanyInfoUpdateRequest,
): Promise<BusinessMypageCompanyInfoUpdateResponse> => {
  try {
    const { data } = await privateHttp.post<{
      statusCode: number;
      message?: string;
      data?: { updatedCount: number };
    }>(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_INFO_UPDATE,
      request,
    );
    if (data.statusCode !== 200) {
      return {
        success: false,
        message: data.message || "회사 정보 업데이트 중 오류가 발생했습니다.",
      };
    }
    return {
      success: true,
      message: data.message || "회사 정보가 성공적으로 업데이트되었습니다.",
      data: data.data,
    };
  } catch (error) {
    console.error(
      "Business mypage company info 업데이트 API 호출 에러:",
      error,
    );
    return {
      success: false,
      message:
        (axios.isAxiosError(error) && error.response?.data?.message) ||
        "회사 정보 업데이트 중 오류가 발생했습니다.",
    };
  }
};

export const useUpdateBusinessMypageCompanyInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageCompanyInfo,
    onSuccess: () => {
      // 회사 정보 업데이트 후 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.companyInfo,
      });
    },
  });
};

const joinBusinessCompanyMember = async (
  request: BusinessCompanyMemberJoinRequest,
): Promise<BusinessCompanyMemberJoinResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_MEMBER_JOIN,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business company member join API 호출 에러:", error);
    throw error;
  }
};

export const useJoinBusinessCompanyMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinBusinessCompanyMember,
    onSuccess: () => {
      // 임직원 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.companyMember,
      });
    },
  });
};

const getBusinessCompanyMemberInfo = async (
  bizUserSeq: number,
): Promise<BusinessCompanyMemberInfoResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_MEMBER_INFO,
      {
        params: { bizUserSeq },
      },
    );
    return (
      data.data || {
        infoBusinessUserDto: {
          dtUpdate: "",
          delYn: "N",
          dtRegist: "",
          bizUserSeq: 0,
          snsId: "",
          cdUserType: "",
          email: "",
          cdSnsType: "",
          companySeq: 0,
          bizUserName: "",
          phoneNumber: "",
          alarmNoticeYn: "N",
          alarmMarketingYn: "N",
          alarmSmsYn: "N",
          alarmEmailYn: "N",
        },
        menus: [],
      }
    );
  } catch (error) {
    console.error("Business company member info API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessCompanyMemberInfo = (bizUserSeq: number) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.companyMemberInfo, bizUserSeq],
    queryFn: () => getBusinessCompanyMemberInfo(bizUserSeq),
    enabled: !!bizUserSeq,
    select: (data) => data,
  });
};

const updateBusinessCompanyMemberPermissions = async (
  request: BusinessUpdateCompanyMemberPermissionsRequest,
) => {
  try {
    const { data } = await privateHttp.put(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_MEMBER_PERMISSIONS,
      request,
    );
    return data;
  } catch (error) {
    console.error(
      "Business company member permissions 업데이트 API 호출 에러:",
      error,
    );
    throw error;
  }
};

export const useUpdateBusinessCompanyMemberPermissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBusinessCompanyMemberPermissions,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...BUSINESS_MYPAGE_QUERY_KEYS.companyMemberInfo,
          variables.bizUserSeq,
        ],
      });
    },
  });
};

const deleteBusinessCompanyMember = async (
  request: BusinessCompanyMemberDeleteRequest,
): Promise<BusinessCompanyMemberDeleteResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_COMPANY_MEMBER_DELETE,
      request,
    );
    return {
      success: true,
      message: data.message || "임직원이 성공적으로 삭제되었습니다.",
      data: data.data,
    };
  } catch (error) {
    console.error("Business company member delete API 호출 에러:", error);
    return {
      success: false,
      message: "임직원 삭제 중 오류가 발생했습니다.",
    };
  }
};

export const useDeleteBusinessCompanyMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBusinessCompanyMember,
    onSuccess: () => {
      // 임직원 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.companyMember,
      });
      // 임직원 상세 정보 캐시도 무효화
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.companyMemberInfo,
      });
    },
  });
};

const getBusinessServiceProvideList = async (): Promise<
  BusinessServiceProvideListResponse[]
> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_SERVICE_PROVIDE_LIST,
    );
    return data.data || [];
  } catch (error) {
    console.error("Business service provide list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessServiceProvideList = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.serviceProvideList],
    queryFn: () => getBusinessServiceProvideList(),
    select: (data) =>
      data || {
        serviceProvides: [],
      },
  });
};

// Business mypage customer list 조회
const getBusinessMypageCustomerList = async (
  params: BusinessMypageCustomerListRequest,
): Promise<BusinessMypageCustomerListResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CUSTOMER_LIST,
      {
        params: {
          searchKeyword: params.searchKeyword,
          reservType: params.reservType,
          page: params.page || 0,
          size: params.size || 10,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage customer list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageCustomerList = (
  params: BusinessMypageCustomerListRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.customerList, params],
    queryFn: () => getBusinessMypageCustomerList(params),
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business mypage customer detail 조회
const getBusinessMypageCustomerDetail = async (
  params: BusinessMypageCustomerDetailRequest,
): Promise<BusinessMypageCustomerDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CUSTOMER_DETAIL,
      {
        params: {
          userSeq: params.userSeq,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage customer detail API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageCustomerDetail = (
  params: BusinessMypageCustomerDetailRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.customerDetail, params],
    queryFn: () => getBusinessMypageCustomerDetail(params),
    enabled: !!params.userSeq,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// ── 매장 전용 펫 메모 / 블랙리스트 (보호자 미노출) ──
export interface PetMemo {
  petSeq: number;
  memoContent: string | null;
  blacklistYn: string | null;
  blacklistReason: string | null;
}

const getPetMemo = async (petSeq: number): Promise<PetMemo> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_PET_MEMO,
    { params: { petSeq } },
  );
  return data.data;
};

export const usePetMemo = (petSeq?: number) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.petMemo, petSeq],
    queryFn: () => getPetMemo(petSeq as number),
    enabled: !!petSeq,
    staleTime: 60 * 1000,
  });
};

const savePetMemo = async (req: PetMemo): Promise<unknown> => {
  const { data } = await privateHttp.put(
    API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_PET_MEMO,
    req,
  );
  if (
    data?.statusCode != null &&
    (data.statusCode < 200 || data.statusCode >= 300)
  ) {
    throw new Error(data?.message || "펫 메모 저장 실패");
  }
  return data;
};

export const useSavePetMemo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: savePetMemo,
    onSuccess: (_data, req) => {
      queryClient.invalidateQueries({
        queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.petMemo, req.petSeq],
      });
    },
  });
};

// Business mypage 고객 반려동물별 이용권 목록 조회
const getBusinessMypageCustomerTicketList = async (
  params: BusinessMypageCustomerTicketListRequest,
): Promise<BusinessMypageCustomerTicketListResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CUSTOMER_TICKET_LIST,
      {
        params: {
          petSeq: params.petSeq,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage customer ticket-list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageCustomerTicketList = (
  params: BusinessMypageCustomerTicketListRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.customerTicketList, params.petSeq],
    queryFn: () => getBusinessMypageCustomerTicketList(params),
    enabled: !!params.petSeq && params.petSeq > 0,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// ── 고객 반려동물별 이용권 사용내역 (구매 묶음 + 회차별 사용일자) ──
export interface CustomerTicketUse {
  ticketUseSeq: number;
  dtUse: string | null; // 차감 기록 시각(표시용 아님)
  dtUsed: string | null; // 실제 이용일(예약 방문일)
  qty: number | null;
  reservType: string | null; // 서비스 타입
  dtExpected: string | null; // 방문예정일
  status: string | null; // 예약 상태
  companyName: string | null;
}
export interface CustomerTicketUsageBundle {
  purchaseTicketSeq: number;
  ticketName: string;
  cdProductType: string | null;
  qty: number | null;
  remainQty: number | null;
  dtRegist: string | null; // 구매일
  dtExpired: string | null;
  status: string | null;
  uses: CustomerTicketUse[];
}
export interface CustomerTicketUsage {
  petSeq: number;
  petName: string | null;
  useCount: number;
  bundles: CustomerTicketUsageBundle[];
}

const getCustomerTicketUsage = async (
  petSeq: number,
  from?: string,
  to?: string,
): Promise<CustomerTicketUsage> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CUSTOMER_TICKET_USAGE,
    { params: { petSeq, from: from || undefined, to: to || undefined } },
  );
  return data.data;
};

/** 펫의 이용권 사용내역. from/to(YYYY-MM-DD) 없으면 전체. */
export const useCustomerTicketUsage = (
  petSeq: number,
  from?: string,
  to?: string,
) =>
  useQuery({
    queryKey: [
      "business",
      "mypage",
      "customerTicketUsage",
      petSeq,
      from ?? "",
      to ?? "",
    ],
    queryFn: () => getCustomerTicketUsage(petSeq, from, to),
    enabled: !!petSeq && petSeq > 0,
    staleTime: 60 * 1000,
  });

// Business mypage contract list 조회
const getBusinessMypageContractList = async (
  params: BusinessMypageContractListRequest,
): Promise<BusinessMypageContractListResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CONTRACT_LIST,
      {
        params: {
          searchKeyword: params.searchKeyword,
          page: params.page || 1,
          size: params.size || 10,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage contract list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageContractList = (
  params: BusinessMypageContractListRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.contractList, params],
    queryFn: () => getBusinessMypageContractList(params),
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business Mypage Contract Detail 조회
const getBusinessMypageContractDetail = async (
  params: BusinessMypageContractDetailRequest,
): Promise<BusinessMypageContractDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_CONTRACT_DETAIL,
      {
        params: {
          histContrSeq: params.histContrSeq,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage contract detail API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageContractDetail = (
  params: BusinessMypageContractDetailRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.contractDetail, params],
    queryFn: () => getBusinessMypageContractDetail(params),
    enabled: !!params.histContrSeq,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business Mypage Business Time 조회
const getBusinessMypageBusinessTime =
  async (): Promise<BusinessMypageBusinessTimeResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_TIME,
      );
      return data;
    } catch (error) {
      console.error("비즈니스 마이페이지 영업시간 조회 API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessMypageBusinessTime = () => {
  return useQuery({
    queryKey: ["business-mypage-business-time"],
    queryFn: getBusinessMypageBusinessTime,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business Mypage Business Time 업데이트
const updateBusinessMypageBusinessTime = async (
  businessTimes: BusinessMypageBusinessTimeRequest[],
): Promise<{ statusCode: number }> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_TIME,
      businessTimes,
    );
    return data;
  } catch (error) {
    console.error(
      "비즈니스 마이페이지 영업시간 업데이트 API 호출 에러:",
      error,
    );
    throw error;
  }
};

export const useBusinessMypageBusinessTimeUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageBusinessTime,
    onSuccess: () => {
      // 성공 시 영업시간 데이터를 다시 조회
      queryClient.invalidateQueries({
        queryKey: ["business-mypage-business-time"],
      });
    },
  });
};

// Business Mypage 임시 휴무일 조회 (yearMonth 미지정 시 오늘 이후 전체)
const getBusinessMypageHolidays = async (
  yearMonth?: string,
): Promise<BusinessMypageHolidayResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.HOLIDAY,
      { params: yearMonth ? { yearMonth } : {} },
    );
    return data;
  } catch (error) {
    console.error("비즈니스 마이페이지 임시 휴무일 조회 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageHolidays = (yearMonth?: string) => {
  return useQuery({
    queryKey: ["business-mypage-holiday", yearMonth ?? "upcoming"],
    queryFn: () => getBusinessMypageHolidays(yearMonth),
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// Business Mypage 임시 휴무일 저장 (여러 날짜 일괄)
const saveBusinessMypageHolidays = async (
  param: BusinessMypageHolidaySaveRequest,
): Promise<BusinessMypageHolidayResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.HOLIDAY,
      param,
    );
    return data;
  } catch (error) {
    console.error("비즈니스 마이페이지 임시 휴무일 저장 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageHolidaySave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveBusinessMypageHolidays,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-mypage-holiday"] });
    },
  });
};

// Business Mypage 임시 휴무일 삭제(휴무 해제)
const deleteBusinessMypageHoliday = async (
  holidaySeq: number,
): Promise<{ statusCode: number }> => {
  try {
    const { data } = await privateHttp.delete(
      `${API_ENDPOINTS.BUSINESS_MYPAGE.HOLIDAY}/${holidaySeq}`,
    );
    return data;
  } catch (error) {
    console.error("비즈니스 마이페이지 임시 휴무일 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageHolidayDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBusinessMypageHoliday,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-mypage-holiday"] });
    },
  });
};

// Business Mypage 예약 마감시간 설정 조회
const getBusinessMypageReserveDeadline =
  async (): Promise<BusinessMypageReserveDeadlineResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.RESERVE_DEADLINE,
      );
      return data;
    } catch (error) {
      console.error(
        "비즈니스 마이페이지 예약 마감시간 조회 API 호출 에러:",
        error,
      );
      throw error;
    }
  };

export const useBusinessMypageReserveDeadline = () => {
  return useQuery({
    queryKey: ["business-mypage-reserve-deadline"],
    queryFn: getBusinessMypageReserveDeadline,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// Business Mypage 예약 마감시간 설정 저장
const saveBusinessMypageReserveDeadline = async (
  param: BusinessMypageReserveDeadlineSettings,
): Promise<{ statusCode: number }> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.RESERVE_DEADLINE,
      param,
    );
    return data;
  } catch (error) {
    console.error(
      "비즈니스 마이페이지 예약 마감시간 저장 API 호출 에러:",
      error,
    );
    throw error;
  }
};

export const useBusinessMypageReserveDeadlineSave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveBusinessMypageReserveDeadline,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["business-mypage-reserve-deadline"],
      });
    },
  });
};

// Business Service Stats 조회
const getBusinessServiceStats = async (
  params: BusinessServiceStatsRequest,
): Promise<BusinessServiceStatsResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_SERVICE_STATS,
      { params },
    );
    return data;
  } catch (error) {
    console.error("Business service stats API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessServiceStats = (
  params: BusinessServiceStatsRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.serviceStats, params],
    queryFn: () => getBusinessServiceStats(params),
    enabled: !!params.yearMonth,
    staleTime: 0, // 탭 진입 시마다 조회
    refetchOnMount: "always",
  });
};

// Business Settlement Info 조회
const getBusinessSettlementInfo = async (
  params: BusinessSettlementInfoRequest,
): Promise<BusinessSettlementInfoResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.BUSINESS_MYPAGE_SETTLEMENT_INFO,
      { params },
    );
    return data;
  } catch (error) {
    console.error("Business settlement info API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessSettlementInfo = (
  params: BusinessSettlementInfoRequest,
) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.settlementInfo, params],
    queryFn: () => getBusinessSettlementInfo(params),
    enabled: !!params.yearMonth,
    staleTime: 0, // 탭 진입 시마다 조회
    refetchOnMount: "always",
  });
};

// Business Mypage Disconnect Company API
const disconnectBusinessCompany =
  async (): Promise<BusinessMypageDisconnectCompanyResponse> => {
    try {
      const { data } =
        await privateHttp.post<BusinessMypageDisconnectCompanyResponse>(
          API_ENDPOINTS.BUSINESS_MYPAGE.DISCONNECT_COMPANY,
        );
      return data;
    } catch (error) {
      console.error("Business mypage disconnect company API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessMypageDisconnectCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectBusinessCompany,
    onSuccess: () => {
      // 회사 연결 해제 후 관련 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.main,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.info,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.companyInfo,
      });
    },
  });
};

// Business Mypage Ticket List 조회
interface TicketListResponse {
  kindergarten: TicketItem[];
  grooming: TicketItem[];
  daycare: TicketItem[];
  hotel: TicketItem[];
}

interface TicketItem {
  productTicketSeq: number;
  companySeq: number;
  cdTicketType: string;
  cdProductType: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  splitCount: number;
  badge: string[];
  activeYn: string;
  dataOrder: number;
  // 데이케어 시간권: 최소 구매/이용 수량 (null이면 제한 없음)
  minPurchaseQty?: number | null;
  minUseQty?: number | null;
}

const getBusinessMypageTicketList = async (): Promise<TicketListResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_LIST,
    );
    return (
      data.data || {
        kindergarten: [],
        grooming: [],
        daycare: [],
        hotel: [],
      }
    );
  } catch (error) {
    console.error("Business mypage ticket list API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageTicketList = () => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.ticketList],
    queryFn: () => getBusinessMypageTicketList(),
    select: (data) =>
      data || {
        kindergarten: [],
        grooming: [],
        daycare: [],
        hotel: [],
      },
  });
};

// Business Mypage Ticket 등록/수정
interface TicketRequest {
  productTicketSeq?: number;
  companySeq?: number;
  cdTicketType: string;
  cdProductType: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  splitCount?: number;
  badge: string[];
  activeYn: string;
  dataOrder: number;
  // 데이케어 시간권: 최소 구매/이용 수량 (null이면 제한 없음)
  minPurchaseQty?: number | null;
  minUseQty?: number | null;
}

interface TicketResponse {
  statusCode: number;
  data?: TicketItem;
  message?: string;
}

const createOrUpdateBusinessMypageTicket = async (
  request: TicketRequest,
): Promise<TicketResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket 등록/수정 API 호출 에러:", error);
    throw error;
  }
};

export const useCreateOrUpdateBusinessMypageTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrUpdateBusinessMypageTicket,
    onSuccess: () => {
      // 이용권 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketList,
      });
    },
  });
};

// Business Mypage Ticket 상세 조회
const getBusinessMypageTicketDetail = async (
  productTicketSeq: number,
): Promise<TicketItem> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_DETAIL,
      {
        params: {
          productTicketSeq,
        },
      },
    );
    return data.data;
  } catch (error) {
    console.error("Business mypage ticket detail API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageTicketDetail = (productTicketSeq?: number) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MYPAGE_QUERY_KEYS.ticketList,
      "detail",
      productTicketSeq,
    ],
    queryFn: () => getBusinessMypageTicketDetail(productTicketSeq!),
    enabled: !!productTicketSeq,
  });
};

// Business Mypage Ticket 삭제
interface TicketDeleteResponse {
  statusCode: number;
  message?: string;
}

const deleteBusinessMypageTicket = async (
  productTicketSeq: number,
): Promise<TicketDeleteResponse> => {
  try {
    const { data } = await privateHttp.delete(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET,
      {
        params: {
          productTicketSeq,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useDeleteBusinessMypageTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBusinessMypageTicket,
    onSuccess: () => {
      // 이용권 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketList,
      });
    },
  });
};

// Business Mypage Ticket 순서 저장
interface TicketOrderRequest {
  productTicketSeq: number;
  companySeq: number;
  cdTicketType: string;
  cdProductType: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  splitCount: number;
  badge: string[];
  activeYn: string;
  dataOrder: number;
  // 데이케어 시간권: 최소 구매/이용 수량 (null이면 제한 없음)
  minPurchaseQty?: number | null;
  minUseQty?: number | null;
}

interface TicketOrderResponse {
  statusCode: number;
  message?: string;
}

const updateBusinessMypageTicketOrder = async (
  request: TicketOrderRequest[],
): Promise<TicketOrderResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ORDER,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket order API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateBusinessMypageTicketOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessMypageTicketOrder,
    onSuccess: () => {
      // 이용권 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketList,
      });
    },
  });
};

// Business Mypage Search User
interface SearchUserRequest {
  searchType: "PHONENUMBER" | "PETNAME";
  keyword: string;
}

interface SearchUserItem {
  dtUpdate: string;
  delYn: string;
  dtRegist: string;
  userSeq: number;
  petSeq: number;
  petName: string;
  cdUserType: string;
  email: string;
  snsId: string;
  cdSnsType: string;
  userName: string;
  nickname: string;
  phoneNumber: string;
  alarmNoticeYn: string;
  alarmMarketingYn: string;
  alarmSmsYn: string;
  alarmEmailYn: string;
}

interface SearchUserResponse {
  statusCode: number;
  data: SearchUserItem[];
  message: string;
}

const searchBusinessMypageUser = async (
  params: SearchUserRequest,
): Promise<SearchUserResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.SEARCH_USER,
      {
        params: {
          searchType: params.searchType,
          keyword: params.keyword,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage search user API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageSearchUser = (params: SearchUserRequest) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.searchUser, params],
    queryFn: () => searchBusinessMypageUser(params),
    enabled: !!params.keyword && params.keyword.trim().length > 0,
    staleTime: 0, // 검색 결과는 항상 최신 데이터를 가져와야 함
    gcTime: 0,
  });
};

/** MY코드/QR 로 발급 대상 회원·펫 조회 (신규 고객 오프라인 발급) */
export interface ResolveMemberCodePet {
  petSeq: number;
  petName: string;
  existingCustomer?: boolean;
}
export interface ResolveMemberCodeResult {
  userSeq: number;
  userName: string;
  phoneNumber: string;
  pets: ResolveMemberCodePet[];
}
export const resolveMemberCode = async (
  code: string,
): Promise<ResolveMemberCodeResult | null> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MYPAGE.RESOLVE_MEMBER_CODE,
    { params: { code } },
  );
  // 404 는 axios 에서 throw 되므로 여기 오면 성공. data.data 반환.
  return data?.data ?? null;
};

/** 계약서 발송 '받는 사람' 검색 — 멍투데이 전 보호자 대상(이름 2글자↑·마스킹).
 *  existingCustomer=계약완료 여부(뱃지), ourCustomer=우리 매장 고객(예약이력, 상단 정렬·뱃지) */
export interface ContractRecipientRow {
  userSeq: number;
  userName: string;
  phoneNumber: string;
  petSeq: number;
  petName: string;
  existingCustomer?: boolean;
  ourCustomer?: boolean;
}
export const searchContractRecipients = async (
  searchType: "PHONENUMBER" | "PETNAME",
  keyword: string,
): Promise<ContractRecipientRow[]> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MYPAGE.CONTRACT_RECIPIENT,
    { params: { searchType, keyword } },
  );
  return data?.data ?? [];
};

// Business Mypage Manual Ticket Issue (이용권 수동 지급)
interface ManualTicketIssueRequest {
  userSeq: number;
  petSeq: number;
  productTicketSeq: number;
  qty: number;
}

interface ManualTicketIssueResponse {
  statusCode: number;
  message?: string;
  data?: any;
}

const issueManualTicket = async (
  request: ManualTicketIssueRequest,
): Promise<ManualTicketIssueResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.MANUAL_TICKET,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage manual ticket issue API 호출 에러:", error);
    throw error;
  }
};

export const useIssueManualTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: issueManualTicket,
    onSuccess: () => {
      // 이용권 목록 캐시 무효화하여 새로운 데이터를 불러오기
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketList,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueList,
      });
      // 수동 발급은 크레딧을 차감하므로 잔액/사용내역 캐시도 함께 갱신
      // (staleTime 30초 때문에 갱신하지 않으면 화면에 옛 잔액이 남는다)
      queryClient.invalidateQueries({ queryKey: ["business-credit"] });
    },
  });
};

// 이용권 발급 내역 목록 (GET /business/mypage/ticket/issue/list, params: keyword, status?)
// status: ISSUE(발급), REVOKE(회수), USE(사용), EXPIRED(만료) - 없으면 전체
export interface TicketIssueListItem {
  purchaseTicketSeq: number;
  userSeq?: number;
  userName: string;
  ticketName: string;
  qty: number;
  remainQty?: number;
  dtRegist: string;
  dtExpired: string;
  paymentType: string;
  status: string;
  cdProductType?: string;
  /** 최초 이용일 방식: 최초 이용일로부터 사용 가능 일수 (null이면 기존 방식) */
  usageDurationDays?: number | null;
  /** 최초 이용(방문)일 — usageDurationDays가 있고 이 값이 null이면 아직 이용 시작 전 */
  dtFirstUse?: string | null;
  /** 구매 티켓의 현재 실제 만료일 (최초 이용일 방식 시작 후 표시용) */
  actualDtExpired?: string | null;
}

export interface TicketIssueListResponse {
  statusCode: number;
  message?: string;
  data?: TicketIssueListItem[];
}

const getTicketIssueList = async (params: {
  userName: string | null;
  status?: "ISSUE" | "REVOKE" | "USE" | "EXPIRED";
}): Promise<TicketIssueListResponse> => {
  try {
    const requestParams: Record<string, string> = {};
    if (params.userName != null && params.userName !== "") {
      requestParams.keyword = params.userName;
    }
    if (params.status) {
      requestParams.status = params.status;
    }
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ISSUE_LIST,
      { params: requestParams },
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket issue list API 호출 에러:", error);
    throw error;
  }
};

export const useTicketIssueList = (params: {
  userName: string | null;
  status?: "ISSUE" | "REVOKE" | "USE" | "EXPIRED";
}) => {
  // queryKey에서 undefined 제외해 캐시 키가 일관되게 구분되도록 함
  const queryKey = [
    ...BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueList,
    params.userName,
    params.status ?? null,
  ] as const;
  return useQuery({
    queryKey,
    queryFn: () => getTicketIssueList(params),
    staleTime: 0,
    gcTime: 0, // 탭 전환 시 캐시 사용 안 함 - 해당 탭 요청 응답만 표시
  });
};

// ── 이용권 발급 현황 요약 (GET /business/mypage/ticket/issue/summary?yearMonth) ──
export interface TicketIssueSummary {
  yearMonth: string;
  issueCount: number;
  issueAmount: number;
  customerCount: number;
  activeCount: number;
  statusIssue: number;
  statusUse: number;
  statusRevoke: number;
  statusExpired: number;
}

const getTicketIssueSummary = async (
  yearMonth?: string,
): Promise<TicketIssueSummary> => {
  const { data } = await privateHttp.get(
    API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ISSUE_SUMMARY,
    { params: yearMonth ? { yearMonth } : {} },
  );
  return data.data;
};

export const useTicketIssueSummary = (yearMonth?: string) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueList,
      "summary",
      yearMonth ?? null,
    ],
    queryFn: () => getTicketIssueSummary(yearMonth),
    staleTime: 30 * 1000,
  });
};

// 이용권 발급 상세 (GET /business/mypage/ticket/issue/detail, params: purchaseTicketSeq)
export interface TicketIssueDetailResponse {
  statusCode: number;
  message?: string;
  data?: TicketIssueListItem;
}

const getTicketIssueDetail = async (params: {
  purchaseTicketSeq: number;
}): Promise<TicketIssueDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ISSUE_DETAIL,
      {
        params: {
          purchaseTicketSeq: params.purchaseTicketSeq,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket issue detail API 호출 에러:", error);
    throw error;
  }
};

export const useTicketIssueDetail = (params: { purchaseTicketSeq: number }) => {
  return useQuery({
    queryKey: [...BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueDetail, params],
    queryFn: () => getTicketIssueDetail(params),
    enabled: !!params.purchaseTicketSeq && params.purchaseTicketSeq > 0,
  });
};

// 이용권 발급 유효기간 수정 (PUT /business/mypage/ticket/issue/expired)
export interface TicketIssueExpiredRequest {
  purchaseTicketSeq: number;
  isRevoke?: boolean;
  date?: string;
}

export interface TicketIssueExpiredResponse {
  statusCode: number;
  message?: string;
  data?: unknown;
}

const updateTicketIssueExpired = async (
  request: TicketIssueExpiredRequest,
): Promise<TicketIssueExpiredResponse> => {
  try {
    const { data } = await privateHttp.put(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ISSUE_EXPIRED,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket issue expired API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateTicketIssueExpired = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTicketIssueExpired,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueList,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueDetail,
      });
    },
  });
};

// 이용권 발급 상태 변경 (PUT /business/mypage/ticket/issue/status, 회수 등)
export interface TicketIssueStatusRequest {
  purchaseTicketSeq: number;
  isRevoke: boolean;
}

export interface TicketIssueStatusResponse {
  statusCode: number;
  message?: string;
  data?: unknown;
}

const updateTicketIssueStatus = async (
  request: TicketIssueStatusRequest,
): Promise<TicketIssueStatusResponse> => {
  try {
    const { data } = await privateHttp.put(
      API_ENDPOINTS.BUSINESS_MYPAGE.TICKET_ISSUE_STATUS,
      request,
    );
    return data;
  } catch (error) {
    console.error("Business mypage ticket issue status API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateTicketIssueStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTicketIssueStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueList,
      });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.ticketIssueDetail,
      });
      // 회수는 차감된 크레딧을 환원하므로 잔액/사용내역 캐시도 함께 갱신
      // (갱신하지 않으면 잔액이 복원됐는데도 화면엔 차감된 값이 계속 보인다)
      queryClient.invalidateQueries({ queryKey: ["business-credit"] });
    },
  });
};

// 이용내역 (GET /business/mypage/usage-history)
export interface BusinessMypageUsageHistoryItem {
  reservationSeq: number;
  petSeq: number;
  fileUrl: string;
  petName: string;
  userName: string;
  productName: string;
  petWeight: string;
  paymentType: string;
  ticketName: string;
}

export interface BusinessMypageUsageHistoryRequest {
  searchKeyword?: string;
  startDate?: string;
  endDate?: string;
}

export interface BusinessMypageUsageHistoryResponse {
  statusCode: number;
  data: BusinessMypageUsageHistoryItem[];
  message?: string;
}

const getBusinessMypageUsageHistory = async (
  params: BusinessMypageUsageHistoryRequest,
): Promise<BusinessMypageUsageHistoryResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_MYPAGE.USAGE_HISTORY,
      {
        params: {
          searchKeyword: params.searchKeyword,
          startDate: params.startDate,
          endDate: params.endDate,
        },
      },
    );
    return data;
  } catch (error) {
    console.error("Business mypage usage history API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessMypageUsageHistory = (
  params: BusinessMypageUsageHistoryRequest,
  options?: { enabled?: boolean; refetchKey?: number },
) => {
  return useQuery({
    queryKey: [
      ...BUSINESS_MYPAGE_QUERY_KEYS.usageHistory,
      params,
      options?.refetchKey,
    ],
    queryFn: () => getBusinessMypageUsageHistory(params),
    enabled: options?.enabled ?? true,
    staleTime: 0, // 조회 버튼 클릭 시마다 재호출
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business Mypage Company Active (사업장 활성화 요청)
const requestCompanyActivation = async (): Promise<{
  statusCode: number;
  message?: string;
}> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_MYPAGE.COMPANY_ACTIVE,
    );
    return data;
  } catch (error) {
    console.error("Business mypage company active API 호출 에러:", error);
    throw error;
  }
};

export const useRequestCompanyActivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestCompanyActivation,
    onSuccess: () => {
      // 활성화 요청 성공 시 메인 데이터 캐시 무효화하여 active 상태 업데이트
      queryClient.invalidateQueries({
        queryKey: BUSINESS_MYPAGE_QUERY_KEYS.main,
      });
    },
  });
};

// ============================================================
// 이용권 설정 - 중복예약 허용 여부 (duple-reserve-setting)
// ============================================================

export interface DupleReserveSettingResponse {
  duplicateReservYn: string; // "Y" | "N"
}

export interface DupleReserveSettingRequest {
  duplicateReservYn: string; // "Y" | "N"
}

const DUPLE_RESERVE_SETTING_QUERY_KEY = [
  "business",
  "mypage",
  "duple-reserve-setting",
] as const;

const getDupleReserveSetting =
  async (): Promise<DupleReserveSettingResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_MYPAGE.DUPLE_RESERVE_SETTING,
      );
      return data.data || { duplicateReservYn: "N" };
    } catch (error) {
      console.error("중복예약 허용 설정 조회 API 호출 에러:", error);
      throw error;
    }
  };

export const useDupleReserveSetting = (enabled: boolean = true) => {
  return useQuery({
    queryKey: DUPLE_RESERVE_SETTING_QUERY_KEY,
    queryFn: () => getDupleReserveSetting(),
    enabled,
    select: (data) => data,
  });
};

const updateDupleReserveSetting = async (
  request: DupleReserveSettingRequest,
) => {
  try {
    const { data } = await privateHttp.patch(
      API_ENDPOINTS.BUSINESS_MYPAGE.DUPLE_RESERVE_SETTING,
      request,
    );
    return data;
  } catch (error) {
    console.error("중복예약 허용 설정 수정 API 호출 에러:", error);
    throw error;
  }
};

export const useUpdateDupleReserveSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDupleReserveSetting,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: DUPLE_RESERVE_SETTING_QUERY_KEY,
      });
    },
  });
};
